import React, { useEffect, useState } from 'react';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';
import { BACKEND_URL } from '../lib/apiBase';

/**
 * Generic legal/policy page renderer. Loads its HTML body from
 * `/api/site-content/{contentKey}?lang=xx`, the same translation pipeline
 * that powers the Sitges Guide. Falls back to the supplied static English
 * HTML if the backend has no content yet.
 */
export function LegalPage({ contentKey, fallbackHtml, testId, titleKey, seoTitle, seoDescription }) {
  const { lang, t } = useLanguage();
  const [html, setHtml] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Only scroll to top on initial mount / when navigating to a different
    // legal page — NOT when the user just switches language while reading.
    window.scrollTo({ top: 0, behavior: 'instant' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/site-content/${contentKey}?lang=${lang}`,
        );
        const data = await res.json();
        const body = (data && data.html) || '';
        if (!cancelled) setHtml(body || fallbackHtml || '');
      } catch (err) {
        console.error(`Failed to load ${contentKey}`, err);
        if (!cancelled) setHtml(fallbackHtml || '');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contentKey, lang, fallbackHtml]);

  return (
    <>
      {seoTitle && (
        <SEOHead
          title={seoTitle}
          description={seoDescription || seoTitle}
        />
      )}
      <main
        className="max-w-[820px] mx-auto px-5 sm:px-8 pt-[212px] pb-20"
        data-testid={testId}
        style={{ fontFamily: "'Outfit', sans-serif", color: '#2d5f8d' }}
      >
        {!loaded ? (
          <div className="flex items-center justify-center py-20 text-[#6b7a8c] text-sm">
            {t('loading')}
          </div>
        ) : html && html.trim() !== '' ? (
          <article
            className="legal-content prose prose-lg max-w-none
                       prose-headings:text-[#1a4d7a] prose-headings:font-bold
                       prose-h1:text-4xl sm:prose-h1:text-5xl prose-h1:tracking-tight
                       prose-h2:text-2xl sm:prose-h2:text-3xl
                       prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:font-semibold
                       prose-p:text-[#2d5f8d] prose-li:text-[#2d5f8d]
                       prose-a:text-[#1a4d7a] prose-a:underline prose-a:font-semibold
                       prose-strong:text-[#1a4d7a]"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="py-16 text-center">
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a4d7a' }}>
              {titleKey ? t(titleKey) : ''}
            </h1>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

export default LegalPage;
